// src/pages/DatevCallback.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, ExternalLink } from 'lucide-react';
import { datevEmployeeService, DatevApp, DatevApiError } from '../services/datev/DatevEmployeeService';
import { supabase } from '../shared/services/supabase';

/**
 * DATEV OAuth Callback Page
 * 
 * Diese Seite empfängt den Authorization Code von DATEV nach erfolgreicher
 * Authentifizierung und tauscht ihn über das Backend gegen einen Access Token.
 */

export const DatevCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('DATEV-Verbindung wird hergestellt...');
  const [customerName, setCustomerName] = useState<string | null>(null);
  // Bei DATEV-Errors: Hilfs-URL und DATEV-Error-Code anzeigen (MUST 4XXer-Behandlung)
  const [helpUrl, setHelpUrl] = useState<string | null>(null);
  const [datevErrorCode, setDatevErrorCode] = useState<string | null>(null);
  // Popup-Modus: aus dem state-Parameter abgeleitet (":popup"). Uebersteht die
  // DATEV-Weiterleitung und ist unabhaengig von window.opener, das der Browser
  // in dieser Konstellation kappt.
  const [isPopupWindow, setIsPopupWindow] = useState(false);

  const handleCallback = useCallback(async () => {
    try {
      // Parameter aus URL extrahieren
      const code = searchParams.get('code');
      const state = searchParams.get('state'); // Format: "customerId:app"
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Fehler von DATEV?
      if (error) {
        throw new Error(errorDescription || error);
      }

      // Code vorhanden?
      if (!code || !state) {
        throw new Error('Authorization Code oder State fehlt');
      }

      // State parsen: "customerId:app" oder "customerId:app:popup"
      const stateParts = state.split(':');
      if (stateParts.length < 2) {
        throw new Error('State-Format ungültig (erwartet: customerId:app)');
      }
      const parsedCustomerId = stateParts[0];
      const parsedApp = stateParts[1];
      const isPopup = stateParts[2] === 'popup';
      setIsPopupWindow(isPopup);

      // App-Wert validieren
      const validApps: DatevApp[] = ['payrollreports', 'exports', 'exchange', 'eau'];
      if (!validApps.includes(parsedApp as DatevApp)) {
        throw new Error('Unbekannte App im State: ' + parsedApp);
      }
      const app = parsedApp as DatevApp;

      // Customer-Daten laden für Anzeige + client_id-Ableitung.
      // Die Berater-/Mandantennummer ist mit offline_access die Quelle fuer
      // den zu verifizierenden Bestand (Token ist via Scope auf genau diese
      // {beraternr}-{mandantnr}-Kombination gepinnt).
      const { data: customer } = await supabase
        .from('customers')
        .select('company, beraternummer, mandantennummer')
        .eq('id', parsedCustomerId)
        .single();

      if (customer) {
        setCustomerName(customer.company);
      }

      setMessage('Authorization Code wird eingelöst...');

      // Code gegen Token tauschen über Backend.
      // Wirft DatevApiError bei DATEV-Fehlern - wird im catch unten in
      // helpUrl/datevErrorCode-State uebernommen.
      await datevEmployeeService.handleAuthCallback(code, parsedCustomerId, app);

      // Bestand-Verifikation vorbereiten.
      //
      // Vor der per-Customer-offline_access-Umstellung (06.05.2026) wurde hier
      // GET /clients aufgerufen, um die freigegebenen Mandanten aufzulisten.
      // Mit offline_access wirft DATEV diesen Endpoint mit HTTP 401 ab
      // ("this offline access token is not authorized to access requested
      // clients"). Der Token ist via Scope (datev:iam:client:<ber>-<man>) auf
      // genau einen Bestand gepinnt - dieser leitet sich direkt aus den
      // customers-Stammdaten ab.
      let clientIdsForVerification: string[] = [];
      if (customer && customer.beraternummer && customer.mandantennummer) {
        clientIdsForVerification.push(`${customer.beraternummer}-${customer.mandantennummer}`);
      }

      // MUST (Langzeit-RT): Pro-Bestand-Berechtigungspruefung via GET /clients/{client-id}.
      // DATEV: "Hier darf erst dann eine erfolgreiche Verbindung angezeigt werden,
      // wenn die Online-API die Zugriffsrechte auf den Ordnungsbegriff/Datenbestand
      // bestätigt hat."
      // Bei nur einem Bestand bleibt der Flow schnell. Bei mehreren wird sequenziell
      // verifiziert; bei 0 Bestaenden uebersprungen.
      if (clientIdsForVerification.length > 0) {
        setMessage(`Berechtigungen werden geprüft (0/${clientIdsForVerification.length})...`);
        const verification = await datevEmployeeService.verifyClientPermissions(
          parsedCustomerId, app, clientIdsForVerification
        );

        if (verification.verified.length === 0) {
          // Kein einziger Bestand verifiziert - das ist kein erfolgreicher Connect.
          throw new Error(
            verification.forbidden.length > 0
              ? `Keine Zugriffsberechtigung auf die freigegebenen Bestände (${verification.forbidden.join(', ')}). Bitte prüfen Sie die Freigaben in DATEV.`
              : 'Berechtigungspruefung fuer alle Bestände fehlgeschlagen. Bitte versuchen Sie es erneut.'
          );
        }

        // Teilweiser Erfolg ist zulaessig: einige Bestaende verifiziert, andere
        // nicht. Verifikation als Hinweis im Success-State anzeigen.
        if (verification.forbidden.length > 0 || verification.errors.length > 0) {
          const warnings: string[] = [];
          if (verification.forbidden.length > 0) {
            warnings.push(`${verification.forbidden.length} Bestand/Bestände ohne Berechtigung: ${verification.forbidden.join(', ')}`);
          }
          if (verification.errors.length > 0) {
            warnings.push(`${verification.errors.length} Bestand/Bestände konnten nicht geprüft werden`);
          }
          setMessage(`Verbindung hergestellt - ${verification.verified.length} Bestand/Bestände verifiziert. Hinweis: ${warnings.join('; ')}`);
        } else {
          setMessage(`DATEV-Verbindung erfolgreich hergestellt! ${verification.verified.length} Bestand/Bestände verifiziert.`);
        }
      } else {
        setMessage('DATEV-Verbindung hergestellt. Hinweis: Keine Bestände gefunden - bitte prüfen Sie die Freigaben in DATEV.');
      }

      setStatus('success');

      if (isPopup) {
        // Popup schliesst sich selbst. window.close() ist fuer per Skript
        // geoeffnete Fenster erlaubt, auch wenn die Fenster-Verbindung gekappt ist.
        setTimeout(() => { try { window.close(); } catch (e) { /* ignorieren */ } }, 800);
      } else {
        // Nach 3 Sekunden zum DATEV-Tab navigieren (bestehendes Verhalten)
        setTimeout(() => {
          navigate('/datev');
        }, 3000);
      }

    } catch (error) {
      console.error('DATEV Callback Error:', error);
      setStatus('error');
      if (error instanceof DatevApiError) {
        setMessage(error.message);
        setHelpUrl(error.helpUrl);
        setDatevErrorCode(error.datevErrorCode);
      } else if (error instanceof Error) {
        setMessage(error.message);
      } else {
        setMessage('Ein unerwarteter Fehler ist aufgetreten');
      }
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    handleCallback();
  }, [handleCallback]);

  const handleRetry = () => {
    if (isPopupWindow) {
      try { window.close(); } catch (e) { /* ignorieren */ }
      return;
    }
    navigate('/datev');
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F9FAFB'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '48px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center'
      }}>
        {/* Logo/Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 8px 0'
          }}>
            DATEV Integration
          </h1>
          {customerName && (
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: 0
            }}>
              für {customerName}
            </p>
          )}
        </div>

        {/* Status Icon */}
        <div style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          {status === 'processing' && (
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#EBF5FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Loader 
                size={32} 
                color="#3B82F6"
                style={{
                  animation: 'spin 1s linear infinite'
                }}
              />
            </div>
          )}
          
          {status === 'success' && (
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#D1FAE5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircle size={32} color="#10B981" />
            </div>
          )}
          
          {status === 'error' && (
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <XCircle size={32} color="#EF4444" />
            </div>
          )}
        </div>

        {/* Status Message */}
        <div style={{ marginBottom: status === 'error' ? '16px' : '32px' }}>
          <p style={{
            fontSize: '16px',
            color: status === 'error' ? '#DC2626' : '#374151',
            margin: 0,
            lineHeight: '1.5'
          }}>
            {message}
          </p>
          {/* DATEV-Error-Code und Hilfs-URL bei 4XX-Fehlern (MUST: Hilfs-URL anzeigen) */}
          {status === 'error' && datevErrorCode && (
            <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#991B1B' }}>
              DATEV-Code: <code style={{ fontFamily: 'monospace' }}>{datevErrorCode}</code>
            </p>
          )}
          {status === 'error' && helpUrl && (
            <p style={{ margin: '12px 0 0 0', fontSize: '14px' }}>
              <a
                href={helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#1E40AF',
                  textDecoration: 'underline',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                DATEV-Hilfe zu diesem Fehler öffnen
                <ExternalLink size={12} />
              </a>
            </p>
          )}
        </div>

        {/* Actions */}
        {status === 'error' && (
          <button
            onClick={handleRetry}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#2563EB';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#3B82F6';
            }}
          >
            {isPopupWindow ? 'Fenster schließen' : 'Zurück zu DATEV'}
          </button>
        )}

        {status === 'processing' && (
          <div style={{
            fontSize: '13px',
            color: '#6B7280'
          }}>
            Bitte warten Sie einen Moment...
          </div>
        )}

        {status === 'success' && (
          <div style={{
            fontSize: '13px',
            color: '#10B981',
            fontWeight: '500'
          }}>
            {isPopupWindow ? 'Dieses Fenster schließt sich automatisch.' : 'Sie werden automatisch weitergeleitet...'}
          </div>
        )}
      </div>

      {/* CSS Animation für Spinner */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};